import { type GlycerinDisplayProductPayload } from '@packages/tracking';
import type { useRouter } from 'next/router';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';
import { getStockValuesFromDimensions } from '../getStockValuesFromDimensions';
import { getGlycerinAvailability } from '../getGlycerinAvailability';
import { getGlycerinPrice } from '../getGlycerinPrice';
import { getGlycerinRatingStatus } from '../getGlycerinRatingStatus';
import { getGlycerinOrigin } from '../getGlycerinOrigin';
import { getGlycerinServices } from '../getGlycerinServices';

const GetGlycerinDisplayProductPayloadFragment = graphql(/* GraphQL */ `
  fragment GetGlycerinDisplayProductPayloadFragment on Product {
    akl
    sku
    orderNumber
    availability {
      ...GetGlycerinAvailabilityFragment
    }
    price {
      ...GetGlycerinPriceFragment
      ...GetGlycerinServicesCurrencyFragment
    }
    seoRatingGetGlycerinDisplayProductPayload: seoRating(locale: $locale) {
      ...GetGlycerinRatingStatusFragment
    }
    aplusGetGlycerinDisplayProductPayload: aplus(language: $lang) @skip(if: $skipAplus) {
      ...GetGlycerinServicesFragment
    }
    ...GetStockValuesFromDimensionsFragment
  }
`);

export type GetGlycerinDisplayProductPayloadProps = {
  data: FragmentType<typeof GetGlycerinDisplayProductPayloadFragment>;
  asPath: ReturnType<typeof useRouter>['asPath'];
};

export const getGlycerinDisplayProductPayload = ({
  data,
  asPath,
}: GetGlycerinDisplayProductPayloadProps): GlycerinDisplayProductPayload => {
  const unmaskedData = getFragmentData(GetGlycerinDisplayProductPayloadFragment, data);
  const {
    akl,
    orderNumber,
    sku,
    price,
    availability,
    seoRatingGetGlycerinDisplayProductPayload,
    aplusGetGlycerinDisplayProductPayload,
  } = unmaskedData;

  const { inStock, outOfStock } = getStockValuesFromDimensions(unmaskedData);

  return {
    productId: akl,
    articleId: orderNumber,
    skuId: sku,
    ...getGlycerinPrice(price),
    ...getGlycerinAvailability(availability),
    ...getGlycerinRatingStatus(seoRatingGetGlycerinDisplayProductPayload),
    ...getGlycerinOrigin(asPath),
    ...getGlycerinServices({ aplus: aplusGetGlycerinDisplayProductPayload, price }),
    variantsInStock: inStock,
    variantsOutOfStock: outOfStock,
  };
};
